// Batch export (§11.13): photos × presets, sequential through the export worker; to the folder
// (exports/<preset subfolder>/) or into one zip. Per-file progress with ETA; Cancel finishes the current
// file then stops; a failed file does not stop the batch (summary lists failures with the reason).
import { applyTemplate, dedupeNames, sanitizeName, stripExt } from '../io/rename';
import { readExif } from '../io/exif';
import { ZipBuilder } from '../io/zip';
import type { MessageKey } from '../i18n';
import { errorKey } from '../i18n/errors';
import type { PhotoFolder } from '../io/fs';
import type { ExportOptions } from '../engine/exportRenderer';
import type { MetadataSettings } from '../io/encode/xmp';
import type { WatermarkSettings } from './watermark';
import type { ExportPreset, Settings } from '../model/types';
import type { LibraryPhoto } from '../state/library';

export const EXT_OF: Record<ExportPreset['format'], string> = { jpeg: 'jpg', png: 'png', tiff: 'tif' };

export type RenameRequest = {
  enabled: boolean;
  template: string;
  client?: string;
  event?: string;
  dishNames?: (string | undefined)[];
};

/**
 * File names for one preset, in photo order. Names are unique within the batch (case-insensitive,
 * `_2`, `_3`), and never equal to an original's name — the write guard refuses those even inside
 * exports/ (§8, §15.1), so an original's name counts as taken.
 */
export function computeNames(
  photos: { name: string }[],
  preset: ExportPreset,
  rename: RenameRequest,
  dates: (string | undefined)[] = [],
  sourceNames: string[] = photos.map((p) => p.name),
): string[] {
  const ext = EXT_OF[preset.format];
  const useRename = rename.enabled || preset.rename.enabled;
  const template = rename.enabled ? rename.template : preset.rename.template;
  const bases = photos.map((p, i) =>
    useRename
      ? applyTemplate(template || '{orig}', {
          orig: stripExt(p.name),
          seq: i + 1,
          client: rename.client,
          event: rename.event,
          date: dates[i],
          menu: rename.dishNames?.[i],
          preset: sanitizeName(preset.name),
        })
      : stripExt(p.name),
  );
  const taken = new Set(sourceNames.map((n) => n.toLowerCase()));
  const used = new Set<string>();
  return dedupeNames(bases).map((b) => {
    let name = `${b}.${ext}`;
    for (let k = 2; taken.has(name.toLowerCase()) || used.has(name.toLowerCase()); k++)
      name = `${b}_${k}.${ext}`;
    used.add(name.toLowerCase());
    return name;
  });
}

/** `reason` is the raw (English) message; the UI shows `reasonKey`, translated (CODE_REVIEW S19). */
export type Failure = {
  photo: string;
  preset: string;
  presetEs?: string;
  contactSheet?: boolean;
  reason: string;
  reasonKey: MessageKey;
};
export type Progress = {
  done: number;
  total: number;
  current: string;
  etaMs: number | null;
  failures: Failure[];
};

export type ExportOne = (
  file: Blob,
  settings: Settings,
  opts: ExportOptions,
  meta: MetadataSettings,
) => Promise<{ bytes: Uint8Array }>;

export type BatchRequest = {
  photos: LibraryPhoto[];
  presets: ExportPreset[];
  destination: 'folder' | 'zip';
  folder: PhotoFolder | null;
  rename: RenameRequest;
  /** Watermark settings; applied to presets with `watermark: true`, or to all when `watermarkAll`. */
  watermark: WatermarkSettings | null;
  watermarkAll: boolean;
  metadataOverride: ExportPreset['metadata'] | null;
  meta: MetadataSettings;
  getSettings: (id: string) => Settings;
  exportOne: ExportOne;
  /** §11.17: builds the contact sheet for the photos in export order (names of the first preset). */
  contactSheet?: (order: { photo: LibraryPhoto; name: string }[]) => Promise<Uint8Array>;
  onProgress?: (p: Progress) => void;
  signal?: { cancelled: boolean };
};

export type BatchResult = {
  written: string[];
  failures: Failure[];
  cancelled: boolean;
  zip: Blob | null;
  ms: number;
};

export function optionsFor(
  p: ExportPreset,
  req: Pick<BatchRequest, 'watermark' | 'watermarkAll' | 'metadataOverride'>,
): ExportOptions {
  return {
    format: p.format,
    bitDepth: p.bitDepth,
    quality: p.quality,
    size: p.size,
    dpi: p.dpi,
    colorSpace: p.colorSpace,
    sharpen: p.sharpen,
    background: p.background,
    metadata: req.metadataOverride ?? p.metadata,
    ...((p.watermark || req.watermarkAll) && req.watermark ? { watermarkSettings: req.watermark } : {}),
  };
}

export async function runBatch(req: BatchRequest): Promise<BatchResult> {
  const t0 = performance.now();
  const useZip = req.destination === 'zip' || !req.folder || req.folder.mode === 'fallback';
  const zip = useZip ? new ZipBuilder() : null;
  const needsDates =
    /\{date\}/.test(req.rename.template) || req.presets.some((p) => /\{date\}/.test(p.rename.template));
  const dates = needsDates
    ? await Promise.all(req.photos.map(async (p) => (await readExif(p.file)).date))
    : [];
  // Every opened original counts as taken, not only the photos in scope (CODE_REVIEW S1).
  const sourceNames = [...(req.folder?.guard.sourceNames() ?? []), ...req.photos.map((p) => p.name)];
  const jobs = req.presets.flatMap((preset) => {
    const names = computeNames(req.photos, preset, req.rename, dates, sourceNames);
    return req.photos.map((photo, i) => ({ photo, preset, name: names[i]! }));
  });
  const written: string[] = [];
  const failures: Failure[] = [];
  let done = 0;
  let cancelled = false;
  const started = performance.now();
  for (const job of jobs) {
    if (req.signal?.cancelled) {
      cancelled = true;
      break;
    }
    const elapsed = performance.now() - started;
    req.onProgress?.({
      done,
      total: jobs.length,
      current: `${job.photo.name} → ${job.preset.subfolder}/${job.name}`,
      etaMs: done ? (elapsed / done) * (jobs.length - done) : null,
      failures,
    });
    try {
      const res = await req.exportOne(
        job.photo.file,
        req.getSettings(job.photo.id),
        optionsFor(job.preset, req),
        req.meta,
      );
      if (zip) {
        zip.add(`${job.preset.subfolder}/${job.name}`, res.bytes);
        written.push(`${job.preset.subfolder}/${job.name}`);
      } else written.push(await req.folder!.writeExport(job.preset.subfolder, job.name, res.bytes));
    } catch (e) {
      failures.push({
        photo: job.photo.name,
        preset: job.preset.name,
        presetEs: job.preset.nameEs,
        reason: (e as Error).message || String(e),
        reasonKey: errorKey(e),
      });
    }
    done++;
  }
  if (!cancelled && req.contactSheet && req.presets.length) {
    const first = req.presets[0]!;
    const names = computeNames(req.photos, first, req.rename, dates, sourceNames);
    try {
      const pdf = await req.contactSheet(req.photos.map((photo, i) => ({ photo, name: names[i]! })));
      if (zip) {
        zip.add(`${first.subfolder}/contact-sheet.pdf`, pdf);
        written.push(`${first.subfolder}/contact-sheet.pdf`);
      } else written.push(await req.folder!.writeExport(first.subfolder, 'contact-sheet.pdf', pdf));
    } catch (e) {
      failures.push({
        photo: '—',
        preset: 'contact sheet',
        contactSheet: true,
        reason: (e as Error).message || String(e),
        reasonKey: errorKey(e),
      });
    }
  }
  req.onProgress?.({ done, total: jobs.length, current: '', etaMs: 0, failures });
  return {
    written,
    failures,
    cancelled,
    zip: zip && written.length ? await zip.finish() : null,
    ms: performance.now() - t0,
  };
}
